import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import Settings from './pages/settings';
import IntegrationsPage from './pages/integrations';
import BillingPage from './pages/billing';
import DealsPage from './pages/deals';
import ContactsPage from './pages/contacts';
import LoginPage from './pages/login';
import EmailsPage from './pages/emails';
import AccountsPage from './pages/accounts';
import Dashboard from './pages/dashboard';
import Pipeline from './pages/pipeline';
import Reports from './pages/reports';
import Activities from './pages/activities';
import SignupPage from "./pages/signup";
import AdminDashboard from "./pages/admin-dashboard";
import UserManagement from "./pages/user-management";
import CustomerManagement from "pages/customer-management";
import CalendarPage from 'pages/calendar';
import ServiceConfigurationPage from 'pages/service-configuration';
import InvoiceList from 'pages/invoice-list';
import InvoiceBuilder from 'pages/invoice-builder';
import NotificationsPage from './pages/notifications';
import NotificationTemplateManagement from './pages/notification-template-management';
import JWTAuthMonitoring from "./pages/jwt-auth-monitoring";
import RoleManagementDashboard from './pages/role-management-dashboard';
import AuditTrail from './pages/audit-trail';
import MasterPromptBoard from './pages/master-prompt-board';
import AIReviewProtocols from './pages/ai-review-protocols';
import TestingValidationRules from './pages/testing-validation-rules';
import ComponentIdManagementDashboard from './pages/component-id-management-dashboard';
import InitialSetupWizard from './pages/initial-setup-wizard';
import KnowledgeBaseHub from './pages/knowledge-base-hub';
import KnowledgeBaseEditor from './pages/knowledge-base-editor';
import SuperAdminProjectBoard from './pages/super-admin-project-board';
import IndustryManagement from './pages/industry-management';
import OrganizationAdminPanel from './pages/organization-admin-panel';
import SystemAuditDashboard from './pages/system-audit-dashboard';
import ErrorMonitoringCenter from './pages/error-monitoring-center';
import MainLayout from './components/layouts/MainLayout';
import { useTenant } from './contexts/TenantContext';
import { useAuth } from './contexts/AuthContext';
import SampleDataManagementDashboard from './pages/sample-data-management-dashboard';
import SampleDataGenerator from './pages/sample-data-generator';
import SystemConnectivityDashboard from './pages/system-connectivity-dashboard';

// Tenant Route Wrapper - Ensures user is on correct subdomain
const TenantRoute = ({ children }) => {
  const { tenant, loading, error, isOnTenantSubdomain } = useTenant();
  const { user } = useAuth();

  // Show loading state while tenant is being loaded
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading organization...</p>
        </div>
      </div>
    );
  }

  // Show error if tenant not found
  if (isOnTenantSubdomain && error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <svg className="w-12 h-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Organization Not Found</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <a href="https://localprocrm.com" className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Go to Main Site
            </a>
          </div>
        </div>
      </div>
    );
  }

  // If on tenant subdomain but no tenant found, show error
  if (isOnTenantSubdomain && !tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <svg className="w-12 h-12 text-yellow-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Invalid Organization</h2>
            <p className="text-gray-600 mb-4">This organization subdomain is not configured.</p>
            <a href="https://localprocrm.com" className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Go to Main Site
            </a>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default function AppRoutes() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          {/* Public routes - no tenant check */}
          <Route path="/" element={<LoginPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          
          {/* Protected routes - with tenant validation */}
          <Route path="/dashboard" element={<TenantRoute><MainLayout pageTitle="Dashboard - CRM"><Dashboard /></MainLayout></TenantRoute>} />
          <Route path="/settings" element={<TenantRoute><MainLayout pageTitle="Settings - CRM"><Settings /></MainLayout></TenantRoute>} />
          <Route path="/integrations" element={<TenantRoute><MainLayout pageTitle="Integrations - CRM"><IntegrationsPage /></MainLayout></TenantRoute>} />
          <Route path="/billing" element={<TenantRoute><MainLayout pageTitle="Billing - CRM"><BillingPage /></MainLayout></TenantRoute>} />
          <Route path="/deals" element={<TenantRoute><MainLayout pageTitle="Deals - CRM"><DealsPage /></MainLayout></TenantRoute>} />
          <Route path="/contacts" element={<TenantRoute><MainLayout pageTitle="Contacts - CRM"><ContactsPage /></MainLayout></TenantRoute>} />
          <Route path="/emails" element={<TenantRoute><MainLayout pageTitle="Emails - CRM"><EmailsPage /></MainLayout></TenantRoute>} />
          <Route path="/accounts" element={<TenantRoute><MainLayout pageTitle="Accounts - CRM"><AccountsPage /></MainLayout></TenantRoute>} />
          <Route path="/pipeline" element={<TenantRoute><MainLayout pageTitle="Pipeline - CRM"><Pipeline /></MainLayout></TenantRoute>} />
          <Route path="/reports" element={<TenantRoute><MainLayout pageTitle="Reports - CRM"><Reports /></MainLayout></TenantRoute>} />
          <Route path="/activities" element={<TenantRoute><MainLayout pageTitle="Activities - CRM"><Activities /></MainLayout></TenantRoute>} />
          <Route path="/admin-dashboard" element={<TenantRoute><MainLayout pageTitle="Admin Dashboard - CRM"><AdminDashboard /></MainLayout></TenantRoute>} />
          <Route path="/user-management" element={<TenantRoute><MainLayout pageTitle="User Management - CRM"><UserManagement /></MainLayout></TenantRoute>} />
          <Route path="/customer-management" element={<TenantRoute><MainLayout pageTitle="Customer Management - CRM"><CustomerManagement /></MainLayout></TenantRoute>} />
          <Route path="/calendar" element={<TenantRoute><MainLayout pageTitle="Calendar - CRM"><CalendarPage /></MainLayout></TenantRoute>} />
          <Route path="/service-configuration" element={<TenantRoute><MainLayout pageTitle="Service Configuration - CRM"><ServiceConfigurationPage /></MainLayout></TenantRoute>} />
          <Route path="/invoice-list" element={<TenantRoute><MainLayout pageTitle="Invoices - CRM"><InvoiceList /></MainLayout></TenantRoute>} />
          <Route path="/invoice-builder" element={<TenantRoute><MainLayout pageTitle="Invoice Builder - CRM"><InvoiceBuilder /></MainLayout></TenantRoute>} />
          <Route path="/notifications" element={<TenantRoute><MainLayout pageTitle="Notifications - CRM"><NotificationsPage /></MainLayout></TenantRoute>} />
          <Route path="/notification-template-management" element={<TenantRoute><MainLayout pageTitle="Notification Templates - CRM"><NotificationTemplateManagement /></MainLayout></TenantRoute>} />
          <Route path="/jwt-auth-monitoring" element={<TenantRoute><MainLayout pageTitle="JWT Auth Monitoring - CRM"><JWTAuthMonitoring /></MainLayout></TenantRoute>} />
          <Route path="/role-management-dashboard" element={<TenantRoute><MainLayout pageTitle="Role Management - CRM"><RoleManagementDashboard /></MainLayout></TenantRoute>} />
          <Route path="/initial-setup-wizard" element={<TenantRoute><MainLayout pageTitle="Initial Setup - CRM"><InitialSetupWizard /></MainLayout></TenantRoute>} />
          <Route path="/testing-validation-rules" element={<TenantRoute><MainLayout pageTitle="Testing & Validation - CRM"><TestingValidationRules /></MainLayout></TenantRoute>} />
          <Route path="/audit-trail" element={<TenantRoute><MainLayout pageTitle="Audit Trail - CRM"><AuditTrail /></MainLayout></TenantRoute>} />
          <Route path="/master-prompt-board" element={<TenantRoute><MainLayout pageTitle="Master Prompt Board - CRM"><MasterPromptBoard /></MainLayout></TenantRoute>} />
          <Route path="/ai-review-protocols" element={<TenantRoute><MainLayout pageTitle="AI Review Protocols - CRM"><AIReviewProtocols /></MainLayout></TenantRoute>} />
          <Route path="/component-id-management-dashboard" element={<TenantRoute><MainLayout pageTitle="Component IDs - CRM"><ComponentIdManagementDashboard /></MainLayout></TenantRoute>} />
          <Route path="/knowledge-base-hub" element={<TenantRoute><MainLayout pageTitle="Knowledge Base - CRM"><KnowledgeBaseHub /></MainLayout></TenantRoute>} />
          <Route path="/knowledge-base-editor" element={<TenantRoute><MainLayout pageTitle="Knowledge Base Editor - CRM"><KnowledgeBaseEditor /></MainLayout></TenantRoute>} />
          <Route path="/super-admin-project-board" element={<TenantRoute><MainLayout pageTitle="Project Board - CRM"><SuperAdminProjectBoard /></MainLayout></TenantRoute>} />
          <Route path="/industry-management" element={<TenantRoute><MainLayout pageTitle="Industry Management - CRM"><IndustryManagement /></MainLayout></TenantRoute>} />
          <Route path="/organization-admin-panel" element={<TenantRoute><MainLayout pageTitle="Organization Admin Panel - CRM"><OrganizationAdminPanel /></MainLayout></TenantRoute>} />
          <Route path="/system-audit-dashboard" element={<TenantRoute><MainLayout pageTitle="System Audit Dashboard - CRM"><SystemAuditDashboard /></MainLayout></TenantRoute>} />
          <Route path="/error-monitoring-center" element={<TenantRoute><MainLayout pageTitle="Error Monitoring Center - CRM"><ErrorMonitoringCenter /></MainLayout></TenantRoute>} />
          <Route path="/sample-data-management-dashboard" element={<TenantRoute><MainLayout pageTitle="Sample Data Management - CRM"><SampleDataManagementDashboard /></MainLayout></TenantRoute>} />
          <Route path="/sample-data-generator" element={<TenantRoute><MainLayout pageTitle="Sample Data Generator - CRM"><SampleDataGenerator /></MainLayout></TenantRoute>} />
          <Route path="/system-connectivity-dashboard" element={<TenantRoute><MainLayout pageTitle="System Connectivity Dashboard - CRM"><SystemConnectivityDashboard /></MainLayout></TenantRoute>} />
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
}